import React, { useState, useEffect } from 'react';
import { useUserDetailsApi } from '../../../hooks/useUserDetails';
import modalStyles from '../../../styles/AccountModal.module.css';
import buttonStyles from '../../../styles/Buttons.module.css';

interface EditProfileProps {
    userDetails: any;
    onProfileUpdate: (updatedUser: any) => void;
    onCancel: () => void;
}

const EditProfile: React.FC<EditProfileProps> = ({ userDetails, onProfileUpdate, onCancel }) => {
    const { updateUserProfile } = useUserDetailsApi();
    // Identity fields
    const [surname, setSurname] = useState('');
    const [fullNames, setFullNames] = useState('');
    const [nickname, setNickname] = useState('');
    const [idNumber, setIdNumber] = useState('');
    const [identificationType, setIdentificationType] = useState<'RSA ID' | 'Passport' | 'Foreign ID'>('RSA ID');
    const [dateOfBirth, setDateOfBirth] = useState('');
    const [gender, setGender] = useState<'Male' | 'Female' | 'Other'>('Other');
    // Contact fields
    const [email, setEmail] = useState('');
    const [mobileNumber, setMobileNumber] = useState('');
    const [landlineNumber, setLandlineNumber] = useState('');
    // Physical address fields
    const [unitNumber, setUnitNumber] = useState('');
    const [complex, setComplex] = useState('');
    const [streetNumber, setStreetNumber] = useState('');
    const [streetName, setStreetName] = useState('');
    const [suburb, setSuburb] = useState('');
    const [city, setCity] = useState('');
    const [postalCode, setPostalCode] = useState('');
    // Postal address fields
    const [postalSameAsResidential, setPostalSameAsResidential] = useState(true);
    const [postalUnitNumber, setPostalUnitNumber] = useState('');
    const [postalComplex, setPostalComplex] = useState('');
    const [postalStreetName, setPostalStreetName] = useState('');
    const [postalStreetNumber, setPostalStreetNumber] = useState('');
    const [postalSuburb, setPostalSuburb] = useState('');
    const [postalCity, setPostalCity] = useState('');
    const [postalPostalCode, setPostalPostalCode] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (userDetails) {
            // Identity fields
            setSurname(userDetails.lastName || '');
            setFullNames(userDetails.fullNames || '');
            setNickname(userDetails.nickname || '');
            setIdNumber(userDetails.idNumber || '');
            setIdentificationType(userDetails.identificationType || 'RSA ID');
            setDateOfBirth(userDetails.dateOfBirth || '');
            setGender(userDetails.gender || 'Other');
            // Contact fields
            setEmail(userDetails.email || '');
            setMobileNumber(userDetails.mobileNumber || '');
            setLandlineNumber(userDetails.landlineNumber || userDetails.homeTelephone || '');
            // Physical address fields
            setUnitNumber(userDetails.unitNumber || '');
            setComplex(userDetails.complex || '');
            setStreetNumber(userDetails.streetNumber || '');
            setStreetName(userDetails.streetName || '');
            setSuburb(userDetails.suburb || '');
            setCity(userDetails.city || '');
            setPostalCode(userDetails.postalCode || '');
            // Postal address fields
            setPostalSameAsResidential(userDetails.postalAddressSameAsResidential !== false);
            setPostalUnitNumber(userDetails.postalUnitNumber || '');
            setPostalComplex(userDetails.postalComplex || '');
            setPostalStreetNumber(userDetails.postalStreetNumber || '');
            setPostalStreetName(userDetails.postalStreetName || '');
            setPostalSuburb(userDetails.postalSuburb || '');
            setPostalCity(userDetails.postalCity || '');
            setPostalPostalCode(userDetails.postalPostalCode || '');
        }
    }, [userDetails]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setSubmitting(true);

        try {
            const updatedUser = await updateUserProfile({
                surname,
                fullNames,
                nickname,
                idNumber,
                identificationType,
                dateOfBirth: dateOfBirth ? new Date(dateOfBirth) : undefined,
                gender,
                email,
                mobileNumber,
                landlineNumber,
                unitNumber,
                complex,
                streetNumber,
                streetName,
                suburb,
                city,
                postalSameAsResidential,
                postalCode,
                postalUnitNumber,
                postalComplex,
                postalStreetNumber,
                postalStreetName,
                postalSuburb,
                postalCity,
                postalPostalCode,
            });
            onProfileUpdate(updatedUser);
            alert('Profile updated successfully!');
        } catch (err: any) {
            setError(err.message || 'Failed to update profile. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className={modalStyles.backdrop}>
            <div className={modalStyles.modal}>
                <div className={modalStyles.header}>
                    <h3 className={modalStyles.title}>Edit Profile</h3>
                    <button
                        type="button"
                        className={modalStyles.closeButton}
                        onClick={onCancel}
                        aria-label="Close"
                    >
                        ×
                    </button>
                </div>
                <form onSubmit={handleSubmit}>
                    <div className={modalStyles.body}>
                        {/* Identity Section */}
                        <h4 className={modalStyles.sectionTitle}>Identity Information</h4>
                        <div className={modalStyles.grid}>
                            <div className={modalStyles.field}>
                                <label className={modalStyles.label} htmlFor="surname">Surname</label>
                                <input
                                    type="text"
                                    id="surname"
                                    value={surname}
                                    onChange={(e) => setSurname(e.target.value)}
                                    required
                                    className={modalStyles.input}
                                />
                            </div>
                            <div className={modalStyles.field}>
                                <label className={modalStyles.label} htmlFor="fullNames">Full Names</label>
                                <input
                                    type="text"
                                    id="fullNames"
                                    value={fullNames}
                                    onChange={(e) => setFullNames(e.target.value)}
                                    required
                                    className={modalStyles.input}
                                />
                            </div>
                            <div className={modalStyles.field}>
                                <label className={modalStyles.label} htmlFor="nickname">Preferred Name (Optional)</label>
                                <input
                                    type="text"
                                    id="nickname"
                                    value={nickname}
                                    onChange={(e) => setNickname(e.target.value)}
                                    className={modalStyles.input}
                                />
                            </div>
                            <div className={modalStyles.field}>
                                <label className={modalStyles.label} htmlFor="identificationType">Identification Type</label>
                                <select
                                    id="identificationType"
                                    value={identificationType}
                                    onChange={(e) => setIdentificationType(e.target.value as 'RSA ID' | 'Passport' | 'Foreign ID')}
                                    required
                                    className={modalStyles.select}
                                >
                                    <option value="RSA ID">RSA ID</option>
                                    <option value="Passport">Passport</option>
                                    <option value="Foreign ID">Foreign ID</option>
                                </select>
                            </div>
                            <div className={modalStyles.field}>
                                <label className={modalStyles.label} htmlFor="idNumber">ID Number</label>
                                <input
                                    type="text"
                                    id="idNumber"
                                    value={idNumber}
                                    onChange={(e) => setIdNumber(e.target.value)}
                                    required
                                    className={modalStyles.input}
                                />
                            </div>
                            <div className={modalStyles.field}>
                                <label className={modalStyles.label} htmlFor="dateOfBirth">Date of Birth</label>
                                <input
                                    type="date"
                                    id="dateOfBirth"
                                    value={dateOfBirth}
                                    onChange={(e) => setDateOfBirth(e.target.value)}
                                    required
                                    className={modalStyles.input}
                                />
                            </div>
                            <div className={modalStyles.field}>
                                <label className={modalStyles.label} htmlFor="gender">Gender</label>
                                <select
                                    id="gender"
                                    value={gender}
                                    onChange={(e) => setGender(e.target.value as 'Male' | 'Female' | 'Other')}
                                    required
                                    className={modalStyles.select}
                                >
                                    <option value="Male">Male</option>
                                    <option value="Female">Female</option>
                                    <option value="Other">Other</option>
                                </select>
                            </div>
                        </div>

                        {/* Contact Section */}
                        <h4 className={modalStyles.sectionTitle}>Contact Information</h4>
                        <div className={modalStyles.grid}>
                            <div className={modalStyles.field}>
                                <label className={modalStyles.label} htmlFor="email">Email</label>
                                <input
                                    type="email"
                                    id="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    className={modalStyles.input}
                                />
                            </div>
                            <div className={modalStyles.field}>
                                <label className={modalStyles.label} htmlFor="mobileNumber">Mobile Number</label>
                                <input
                                    type="tel"
                                    id="mobileNumber"
                                    value={mobileNumber}
                                    onChange={(e) => setMobileNumber(e.target.value)}
                                    required
                                    maxLength={10}
                                    minLength={10}
                                    className={modalStyles.input}
                                />
                            </div>
                            <div className={modalStyles.field}>
                                <label className={modalStyles.label} htmlFor="landlineNumber">Landline Number (Optional)</label>
                                <input
                                    type="tel"
                                    id="landlineNumber"
                                    value={landlineNumber}
                                    onChange={(e) => setLandlineNumber(e.target.value)}
                                    className={modalStyles.input}
                                />
                            </div>
                        </div>

                        {/* Physical Address Section */}
                        <h4 className={modalStyles.sectionTitle}>Physical Address</h4>
                        <div className={modalStyles.grid}>
                            <div className={modalStyles.field}>
                                <label className={modalStyles.label} htmlFor="unitNumber">Unit Number (Optional)</label>
                                <input
                                    type="text"
                                    id="unitNumber"
                                    value={unitNumber}
                                    onChange={(e) => setUnitNumber(e.target.value)}
                                    className={modalStyles.input}
                                />
                            </div>
                            <div className={modalStyles.field}>
                                <label className={modalStyles.label} htmlFor="complex">Complex Name (Optional)</label>
                                <input
                                    type="text"
                                    id="complex"
                                    value={complex}
                                    onChange={(e) => setComplex(e.target.value)}
                                    className={modalStyles.input}
                                />
                            </div>
                            <div className={modalStyles.field}>
                                <label className={modalStyles.label} htmlFor="streetNumber">Street Number</label>
                                <input
                                    type="text"
                                    id="streetNumber"
                                    value={streetNumber}
                                    onChange={(e) => setStreetNumber(e.target.value)}
                                    required
                                    className={modalStyles.input}
                                />
                            </div>
                            <div className={modalStyles.field}>
                                <label className={modalStyles.label} htmlFor="streetName">Street Name</label>
                                <input
                                    type="text"
                                    id="streetName"
                                    value={streetName}
                                    onChange={(e) => setStreetName(e.target.value)}
                                    required
                                    className={modalStyles.input}
                                />
                            </div>
                            <div className={modalStyles.field}>
                                <label className={modalStyles.label} htmlFor="suburb">Suburb</label>
                                <input
                                    type="text"
                                    id="suburb"
                                    value={suburb}
                                    onChange={(e) => setSuburb(e.target.value)}
                                    required
                                    className={modalStyles.input}
                                />
                            </div>
                            <div className={modalStyles.field}>
                                <label className={modalStyles.label} htmlFor="city">City</label>
                                <input
                                    type="text"
                                    id="city"
                                    value={city}
                                    onChange={(e) => setCity(e.target.value)}
                                    required
                                    className={modalStyles.input}
                                />
                            </div>
                            <div className={modalStyles.field}>
                                <label className={modalStyles.label} htmlFor="postalCode">Postal Code</label>
                                <input
                                    type="text"
                                    id="postalCode"
                                    value={postalCode}
                                    onChange={(e) => setPostalCode(e.target.value)}
                                    required
                                    maxLength={4}
                                    minLength={4}
                                    className={modalStyles.input}
                                />
                            </div>
                        </div>

                        {/* Postal Address Section */}
                        <h4 className={modalStyles.sectionTitle}>Postal Address</h4>
                        <label className={modalStyles.checkboxRow}>
                            <input
                                type="checkbox"
                                checked={postalSameAsResidential}
                                onChange={(e) => setPostalSameAsResidential(e.target.checked)}
                            />
                            Postal address same as residential
                        </label>
                        {!postalSameAsResidential && (
                            <div className={modalStyles.grid} style={{ marginTop: 18 }}>
                                <div className={modalStyles.field}>
                                    <label className={modalStyles.label} htmlFor="postalUnitNumber">Unit Number (Optional)</label>
                                    <input
                                        type="text"
                                        id="postalUnitNumber"
                                        value={postalUnitNumber}
                                        onChange={(e) => setPostalUnitNumber(e.target.value)}
                                        className={modalStyles.input}
                                    />
                                </div>
                                <div className={modalStyles.field}>
                                    <label className={modalStyles.label} htmlFor="postalComplex">Complex (Optional)</label>
                                    <input
                                        type="text"
                                        id="postalComplex"
                                        value={postalComplex}
                                        onChange={(e) => setPostalComplex(e.target.value)}
                                        className={modalStyles.input}
                                    />
                                </div>
                                <div className={modalStyles.field}>
                                    <label className={modalStyles.label} htmlFor="postalStreetNumber">Street Number</label>
                                    <input
                                        type="text"
                                        id="postalStreetNumber"
                                        value={postalStreetNumber}
                                        onChange={(e) => setPostalStreetNumber(e.target.value)}
                                        className={modalStyles.input}
                                    />
                                </div>
                                <div className={modalStyles.field}>
                                    <label className={modalStyles.label} htmlFor="postalStreetName">Street Name</label>
                                    <input
                                        type="text"
                                        id="postalStreetName"
                                        value={postalStreetName}
                                        onChange={(e) => setPostalStreetName(e.target.value)}
                                        className={modalStyles.input}
                                    />
                                </div>
                                <div className={modalStyles.field}>
                                    <label className={modalStyles.label} htmlFor="postalSuburb">Suburb</label>
                                    <input
                                        type="text"
                                        id="postalSuburb"
                                        value={postalSuburb}
                                        onChange={(e) => setPostalSuburb(e.target.value)}
                                        className={modalStyles.input}
                                    />
                                </div>
                                <div className={modalStyles.field}>
                                    <label className={modalStyles.label} htmlFor="postalCity">City</label>
                                    <input
                                        type="text"
                                        id="postalCity"
                                        value={postalCity}
                                        onChange={(e) => setPostalCity(e.target.value)}
                                        className={modalStyles.input}
                                    />
                                </div>
                                <div className={modalStyles.field}>
                                    <label className={modalStyles.label} htmlFor="postalPostalCode">Postal Code</label>
                                    <input
                                        type="text"
                                        id="postalPostalCode"
                                        value={postalPostalCode}
                                        onChange={(e) => setPostalPostalCode(e.target.value)}
                                        maxLength={4}
                                        minLength={4}
                                        className={modalStyles.input}
                                    />
                                </div>
                            </div>
                        )}

                        {error && <div className={modalStyles.error}>{error}</div>}
                    </div>
                    <div className={modalStyles.footer}>
                        <button
                            type="button"
                            onClick={onCancel}
                            className={`${buttonStyles.button} ${buttonStyles.secondary}`}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={submitting}
                            className={`${buttonStyles.button} ${buttonStyles.primary}`}
                        >
                            {submitting ? 'Updating...' : 'Update Profile'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EditProfile;